import * as React from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface SelectOption {
  label: string;
  value: string;
}

export interface SearchableSelectProps {
  options: string[] | SelectOption[];
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  badgeVariant?: "default" | "blue" | "green" | "purple" | "dark" | "primary" | "yellow" | "red" | "outline";
  clearable?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  isPending?: boolean;
}

export const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  className,
  badgeVariant = "default",
  clearable = false,
  disabled = false,
  autoFocus = false,
  onKeyDown,
  isPending = false,
}: SearchableSelectProps) => {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [focusedIndex, setFocusedIndex] = React.useState(-1);
  
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  
  // Normalize options to always have label and value
  const normalizedOptions = React.useMemo(() => {
    return options.map(option => {
      if (typeof option === 'string') {
        return { label: option, value: option };
      }
      return option;
    });
  }, [options]);

  const filteredOptions = React.useMemo(() => {
    if (!searchTerm) return normalizedOptions;
    return normalizedOptions.filter(option => 
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [normalizedOptions, searchTerm]);

  const currentOption = normalizedOptions.find(option => option.value === value);
  const displayValue = currentOption?.label || value;

  // Handle option selection
  const handleSelect = React.useCallback((selectedValue: string) => {
    console.log('SearchableSelect: Selecting value:', selectedValue);
    onChange(selectedValue);
    setOpen(false);
    setSearchTerm("");
    setFocusedIndex(-1);
  }, [onChange]);

  const handleClear = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setOpen(false);
  }, [onChange]);

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    if (onKeyDown) {
      onKeyDown(e);
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          setOpen(true);
        } else {
          setFocusedIndex(prev => 
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (open) {
          setFocusedIndex(prev => 
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (open && focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          handleSelect(filteredOptions[focusedIndex].value);
        } else if (!open) {
          setOpen(true);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        setFocusedIndex(-1);
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  }, [onKeyDown, open, focusedIndex, filteredOptions, handleSelect]);

  // Focus search input when opening
  React.useEffect(() => {
    if (open && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [open]);

  React.useEffect(() => {
    setFocusedIndex(-1);
  }, [searchTerm]);

  React.useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setFocusedIndex(-1);
    }
  }, [open]);

  // Map badge variants to classes
  const getBadgeClasses = React.useCallback(() => {
    switch (badgeVariant) {
      case "blue":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "green":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "purple":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400";
      case "dark":
        return "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
      case "primary":
        return "bg-primary/10 text-primary dark:bg-primary/20";
      case "yellow":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
      case "red":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case "outline":
        return "bg-transparent border border-gray-300 text-gray-800 dark:border-gray-600 dark:text-gray-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
    }
  }, [badgeVariant]);

  return (
    <div className={cn(
      "relative w-full transition-all duration-200",
      isPending && "ring-1 ring-blue-500/50 bg-blue-50/50 dark:bg-blue-900/20 rounded",
      className
    )}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            onKeyDown={handleKeyDown}
            className={cn(
              "relative flex h-8 w-full items-center justify-between rounded-md border px-2 py-1 text-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50",
              "bg-background border-input text-foreground",
              "hover:bg-background hover:border-input transition-colors",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            autoFocus={autoFocus}
            disabled={disabled}
          >
            <div className="flex items-center gap-1 flex-1 min-w-0 pr-2">
              {value && displayValue ? (
                <Badge className={cn("font-normal text-xs px-1.5 py-0.5 max-w-full", getBadgeClasses())}>
                  <span className="truncate block max-w-full" title={displayValue}>
                    {displayValue}
                  </span>
                  {clearable && (
                    <X 
                      className="ml-1 h-2.5 w-2.5 cursor-pointer flex-shrink-0 hover:text-red-500" 
                      onClick={handleClear} 
                    />
                  )}
                </Badge>
              ) : (
                <span className="text-muted-foreground text-xs truncate">
                  {placeholder}
                </span>
              )}
            </div>
            
            <ChevronDown className={cn(
              "h-3 w-3 text-muted-foreground transition-transform duration-200 flex-shrink-0",
              open && "transform rotate-180"
            )} />
          </button>
        </PopoverTrigger>
        
        <PopoverContent 
          className="w-full p-0 max-h-60 overflow-hidden z-[9999] bg-background border-border"
          align="start"
          side="bottom"
          sideOffset={4}
          style={{ 
            zIndex: 9999, 
            position: 'relative',
            pointerEvents: 'auto'
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          }}
        >
          <div className="p-2 border-b border-border">
            <div className="flex items-center border rounded-md bg-background border-input">
              <Search className="h-3 w-3 mx-2 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-7 text-xs bg-transparent"
                onKeyDown={handleKeyDown}
              />
            </div>
          </div>
          
          <div className="overflow-y-auto max-h-48 bg-background">
            <div className="p-1">
              {clearable && (
                <div
                  className="flex items-center justify-between px-2 py-1.5 cursor-pointer rounded-sm text-xs transition-colors hover:bg-accent text-muted-foreground border-b border-border mb-1"
                  onClick={() => handleSelect("")}
                >
                  <span className="truncate max-w-[90%] italic">Clear selection</span>
                  <X className="h-3 w-3 flex-shrink-0" />
                </div>
              )}
              {filteredOptions.length === 0 ? (
                <div className="py-2 px-3 text-xs text-center text-muted-foreground">
                  No results found
                </div>
              ) : (
                filteredOptions.map((option, index) => (
                  <div
                    key={option.value}
                    className={cn(
                      "flex items-center justify-between px-2 py-1.5 cursor-pointer rounded-sm text-xs transition-colors",
                      index === focusedIndex 
                        ? "bg-accent text-accent-foreground"
                        : value === option.value 
                          ? "bg-accent/50 text-accent-foreground"
                          : "hover:bg-accent/50 text-foreground"
                    )}
                    onClick={() => handleSelect(option.value)}
                    onMouseEnter={() => setFocusedIndex(index)}
                  >
                    <span className="truncate max-w-[90%]" title={option.label}>
                      {option.label}
                    </span>
                    {value === option.value && (
                      <Check className="h-3 w-3 flex-shrink-0 text-primary" />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
      
      {isPending && (
        <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
      )}
    </div>
  );
};
